import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X } from 'lucide-react';
import { useRestaurantSettings } from '@/features/restaurant/useRestaurantSettings';
import {
  grantChatbotConsent,
  hasChatbotConsent,
} from '@/features/chatbot/consent';
import {
  noteRouteChange,
  trackChatbotEvent,
  trackFirstInteractionOnce,
} from '@/features/chatbot/tracking';
import {
  attachWidgetDomListeners,
  injectChatbotScript,
  isChatbotScriptLoaded,
  openChatWindow,
  setWidgetVisible,
} from '@/features/chatbot/widgetLoader';

type LauncherState = 'placeholder' | 'card' | 'loading' | 'active' | 'error';

/** widget_loaded einmal pro Browser-Session (Tab). */
function markLoadedOnce(): boolean {
  try {
    if (sessionStorage.getItem('sizilianer-chatbot-loaded')) return false;
    sessionStorage.setItem('sizilianer-chatbot-loaded', '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Zwei-Klick-Consent-Launcher für das Baroon-AI-Chat-Widget.
 *
 * Ohne Einwilligung rendert er einen eigenen Chat-Button (kein Vendor-Kontakt,
 * keine Datenerhebung). Der erste Klick zeigt die Consent-Karte; erst die
 * Zustimmung lädt das Script und öffnet den Chat direkt. Mit gespeicherter
 * Einwilligung lädt das Widget sofort (requestIdleCallback — LCP schonen).
 *
 * Wird nur in PublicPageTemplate gemountet — /admin/* bekommt nie das Script;
 * beim SPA-Wechsel dorthin blendet der Cleanup das Widget aus.
 */
export function ChatbotConsentLauncher() {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: settings } = useRestaurantSettings();
  const [state, setState] = useState<LauncherState>(() =>
    hasChatbotConsent() ? 'active' : 'placeholder',
  );
  const cardRef = useRef<HTMLDivElement>(null);

  const enabled = settings?.chatbot_enabled !== false;

  useEffect(() => {
    // Reihenfolge zwingend: erst die Uhr für die neue Seite stellen, dann
    // melden — sonst trüge das Event die Verweildauer der VORseite.
    noteRouteChange();
    // Seitenverlauf (0076). trackChatbotEvent prüft selbst auf Einwilligung,
    // ohne Opt-in passiert hier also nichts.
    trackChatbotEvent('page_view');
  }, [location.pathname]);

  // Bestehende Einwilligung: Widget im Idle nachladen, sichtbar halten.
  useEffect(() => {
    if (!enabled || state !== 'active') return;
    let cancelled = false;
    const load = () => {
      injectChatbotScript()
        .then(() => {
          if (cancelled) return;
          attachWidgetDomListeners();
          setWidgetVisible(true);
          if (markLoadedOnce()) trackChatbotEvent('widget_loaded');
        })
        .catch(() => {
          if (!cancelled) setState('error');
        });
    };
    if (isChatbotScriptLoaded()) {
      attachWidgetDomListeners();
      setWidgetVisible(true);
    } else if ('requestIdleCallback' in window) {
      window.requestIdleCallback(load, { timeout: 3000 });
    } else {
      setTimeout(load, 500);
    }
    return () => {
      cancelled = true;
      // SPA-Wechsel (auch nach /admin): Vendor-Button ausblenden; die
      // nächste Public-Seite blendet ihn wieder ein.
      setWidgetVisible(false);
    };
  }, [enabled, state]);

  // Consent-Karte: Escape schließt, Fokus auf die Karte (A11y).
  useEffect(() => {
    if (state !== 'card') return;
    cardRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setState('placeholder');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  if (!enabled) return null;

  const accept = () => {
    grantChatbotConsent();
    trackChatbotEvent('consent_given');
    trackFirstInteractionOnce();
    setState('loading');
    injectChatbotScript()
      .then(() => {
        attachWidgetDomListeners();
        setWidgetVisible(true);
        if (markLoadedOnce()) trackChatbotEvent('widget_loaded');
        openChatWindow();
        setState('active');
      })
      .catch(() => setState('error'));
  };

  // Vendor-Button übernimmt — nichts eigenes mehr rendern.
  if (state === 'active') return null;

  return (
    <div className="fixed right-6 z-40 bottom-[calc(24px+var(--sticky-call-bar-h,0px)+env(safe-area-inset-bottom,0px))]">
      {(state === 'card' || state === 'error') && (
        <div
          ref={cardRef}
          tabIndex={-1}
          role="dialog"
          aria-label={t('chatbot.consent.title')}
          className="absolute bottom-[76px] right-0 w-[300px] max-w-[calc(100vw-48px)] bg-paper border border-rule rounded-md shadow-xl p-4 outline-none"
        >
          <div className="flex items-start justify-between gap-2">
            <p className="font-sans text-sm font-semibold text-ink-2">
              {state === 'error'
                ? t('chatbot.consent.errorTitle')
                : t('chatbot.consent.title')}
            </p>
            <button
              type="button"
              onClick={() => setState('placeholder')}
              aria-label={t('common.cancel')}
              className="shrink-0 -mt-1 -mr-1 p-1 text-ink-2/60 hover:text-ink-2 transition-colors"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>
          {state === 'error' ? (
            <p className="mt-2 font-sans text-xs leading-relaxed text-ink-2/80">
              {t('chatbot.consent.error')}
            </p>
          ) : (
            <>
              <p className="mt-2 font-sans text-xs leading-relaxed text-ink-2/80">
                {t('chatbot.consent.body')}{' '}
                <Link to="/datenschutz" className="underline hover:text-gold-2">
                  {t('chatbot.consent.privacy')}
                </Link>
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={accept}
                  className="flex-1 min-h-10 bg-gold-2 border-[1.5px] border-gold-2 text-paper font-sans text-xs tracking-[0.12em] font-semibold uppercase rounded-sm hover:bg-ink-2 hover:border-ink-2 transition-colors"
                >
                  {t('chatbot.consent.accept')}
                </button>
                <button
                  type="button"
                  onClick={() => setState('placeholder')}
                  className="min-h-10 px-3 border-[1.5px] border-rule text-ink-2/70 font-sans text-xs rounded-sm hover:border-ink-2 hover:text-ink-2 transition-colors"
                >
                  {t('chatbot.consent.decline')}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() =>
          setState((s) => (s === 'card' ? 'placeholder' : 'card'))
        }
        disabled={state === 'loading'}
        aria-label={t('chatbot.launcherLabel')}
        aria-expanded={state === 'card'}
        className="flex size-16 items-center justify-center rounded-full bg-gold-2 text-paper shadow-[0_12px_30px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(0,0,0,0.3)] transition-all disabled:opacity-60"
      >
        {state === 'loading' ? (
          <span
            className="size-5 rounded-full border-2 border-paper/40 border-t-paper animate-spin"
            aria-hidden="true"
          />
        ) : (
          <MessageCircle className="size-7" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
